import {
  BadRequestException,
  Body,
  Controller,
  Get,
  Injectable,
  Module,
  Post,
  ServiceUnavailableException,
} from '@nestjs/common';
import { ApiBearerAuth, ApiTags } from '@nestjs/swagger';
import { Type } from 'class-transformer';
import {
  ArrayMinSize,
  IsArray,
  IsDateString,
  IsInt,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Min,
  ValidateNested,
} from 'class-validator';
import { MovementType } from '@sakura/prisma/enums';
import { Prisma } from '@sakura/prisma';
import {
  CurrentUser,
  CurrentUserData,
  Permissions,
  Public,
} from '../common/security';
import { PrismaService } from '../prisma/prisma.module';

class ReturnItemDto {
  @IsUUID()
  saleItemId!: string;
  @IsInt()
  @Min(1)
  quantity!: number;
  @IsOptional()
  @IsNumber()
  @Min(0)
  amount?: number;
}

class CreateReturnDto {
  @IsString()
  returnNumber!: string;
  @IsUUID()
  saleId!: string;
  @IsOptional()
  @IsString()
  reason?: string;
  @IsOptional()
  @IsString()
  returnType?: string;
  @IsArray()
  @ArrayMinSize(1)
  @ValidateNested({ each: true })
  @Type(() => ReturnItemDto)
  items!: ReturnItemDto[];
}

@Injectable()
export class ReturnsService {
  constructor(private readonly prisma: PrismaService) {}

  list() {
    return this.prisma.return.findMany({
      include: { items: true, sale: true },
      orderBy: { createdAt: 'desc' },
    });
  }

  create(dto: CreateReturnDto) {
    return this.prisma.$transaction(async (tx) => {
      const sale = await tx.sale.findUniqueOrThrow({
        where: { id: dto.saleId },
        include: { items: true },
      });
      const isDamage = (dto.returnType ?? '').toLowerCase() === 'damage';
      const prepared: {
        saleItemId: string;
        productId: string;
        batchId: string;
        quantity: number;
        amount: number;
      }[] = [];
      const quantities = new Map<string, number>();

      for (const requested of dto.items) {
        const item = sale.items.find(
          (candidate) => candidate.id === requested.saleItemId,
        );
        if (!item?.batchId) {
          throw new BadRequestException('Invalid or unbatched sale item');
        }
        const returned = await tx.returnItem.aggregate({
          where: { saleItemId: item.id },
          _sum: { quantity: true },
        });
        const requestedInThisReturn =
          (quantities.get(item.id) ?? 0) + requested.quantity;
        if (
          (returned._sum.quantity ?? 0) + requestedInThisReturn >
          item.quantity
        ) {
          throw new BadRequestException(
            'Return quantity exceeds sold quantity',
          );
        }
        quantities.set(item.id, requestedInThisReturn);
        prepared.push({
          saleItemId: item.id,
          productId: item.productId,
          batchId: item.batchId,
          quantity: requested.quantity,
          amount:
            Math.round(
              ((Number(item.total) / item.quantity) * requested.quantity +
                Number.EPSILON) *
                100,
            ) / 100,
        });
      }

      const createdReturn = await tx.return.create({
        data: {
          returnNumber: dto.returnNumber,
          saleId: dto.saleId,
          reason: dto.reason,
          returnType: dto.returnType,
          total: prepared.reduce((sum, item) => sum + item.amount, 0),
          items: {
            create: prepared.map((item) => ({
              saleItemId: item.saleItemId,
              productId: item.productId,
              quantity: item.quantity,
              amount: item.amount,
            })),
          },
        },
        include: { items: true },
      });

      for (const item of prepared) {
        if (isDamage) {
          // Damage returns are not restocked into sellable inventory.
          await tx.stockMovement.create({
            data: {
              productId: item.productId,
              branchId: sale.branchId,
              type: MovementType.DAMAGE,
              quantity: 0,
              balanceAfter: (
                await tx.productBatch.findUniqueOrThrow({
                  where: { id: item.batchId },
                })
              ).quantity,
              referenceType: 'Return',
              referenceId: createdReturn.id,
              notes: 'Damage return — not restocked',
            },
          });
          continue;
        }
        const batch = await tx.productBatch.update({
          where: { id: item.batchId },
          data: { quantity: { increment: item.quantity } },
        });
        await tx.stockMovement.create({
          data: {
            productId: item.productId,
            branchId: sale.branchId,
            type: MovementType.SALE_RETURN,
            quantity: item.quantity,
            balanceAfter: batch.quantity,
            referenceType: 'Return',
            referenceId: createdReturn.id,
          },
        });
      }

      const returnedTotal = await tx.return.aggregate({
        where: { saleId: sale.id },
        _sum: { total: true },
      });
      const netSaleTotal = Math.max(
        0,
        Number(sale.total) - Number(returnedTotal._sum.total ?? 0),
      );
      await tx.sale.update({
        where: { id: sale.id },
        data: {
          dueAmount:
            Math.round(
              (Math.max(0, netSaleTotal - Number(sale.paidAmount)) +
                Number.EPSILON) *
                100,
            ) / 100,
        },
      });
      return createdReturn;
    });
  }
}

class BillingCollectionDto {
  @IsString()
  receiptNo!: string;
  @IsDateString()
  paymentDate!: string;
  @IsString()
  section!: string;
  @IsString()
  partyId!: string;
  @IsString()
  partyName!: string;
  @IsOptional()
  @IsString()
  designation?: string;
  @IsOptional()
  @IsString()
  department?: string;
  @IsOptional()
  @IsString()
  address?: string;
  @IsNumber()
  @Min(0)
  totalDue!: number;
  @IsNumber()
  @Min(0)
  receivedAmount!: number;
  @IsOptional()
  @IsString()
  remarks?: string;
  @IsUUID()
  branchId!: string;
}

@ApiTags('billing-collections')
@ApiBearerAuth()
@Controller('billing-collections')
class BillingCollectionsController {
  constructor(private readonly prisma: PrismaService) {}

  @Get()
  list() {
    return this.prisma.billingCollection.findMany({
      orderBy: { paymentDate: 'desc' },
    });
  }

  @Post()
  create(@Body() dto: BillingCollectionDto) {
    const remainingDue = Math.max(0, dto.totalDue - dto.receivedAmount);
    const advanceAmount = Math.max(0, dto.receivedAmount - dto.totalDue);
    const status =
      advanceAmount > 0 ? 'Advance' : remainingDue > 0 ? 'Partial' : 'Paid';
    return this.prisma.billingCollection.create({
      data: {
        ...dto,
        paymentDate: new Date(dto.paymentDate),
        remainingDue,
        advanceAmount,
        status,
      },
    });
  }
}

class StockAdjustmentLineDto {
  @IsUUID()
  productId!: string;
  @IsString()
  productName!: string;
  @IsString()
  unit!: string;
  @IsNumber()
  unitPrice!: number;
  @IsNumber()
  mrp!: number;
  @IsInt()
  totalQty!: number;
  @IsInt()
  bonusQty!: number;
  @IsInt()
  netQty!: number;
  @IsNumber()
  vat!: number;
  @IsNumber()
  commission!: number;
  @IsNumber()
  totalTp!: number;
}

class StockAdjustmentDto {
  @IsString()
  invoiceNo!: string;
  @IsDateString()
  invoiceDate!: string;
  @IsOptional()
  @IsUUID()
  companyId?: string;
  @IsUUID()
  branchId!: string;
  @IsString()
  reason!: string;
  @IsArray()
  @ArrayMinSize(1)
  @ValidateNested({ each: true })
  @Type(() => StockAdjustmentLineDto)
  lines!: StockAdjustmentLineDto[];
}

class StockAuditDto {
  @IsDateString()
  auditDate!: string;
  @IsUUID()
  productId!: string;
  @IsOptional()
  @IsUUID()
  companyId?: string;
  @IsUUID()
  branchId!: string;
  @IsString()
  medicineName!: string;
  @IsNumber()
  @Min(0)
  averagePrice!: number;
  @IsInt()
  @Min(0)
  physicalQty!: number;
}

@Injectable()
class StockRecordsService {
  constructor(private readonly prisma: PrismaService) {}

  listAdjustments() {
    return this.prisma.stockAdjustment.findMany({
      include: {
        company: true,
        createdBy: {
          select: {
            id: true,
            username: true,
            firstName: true,
            lastName: true,
          },
        },
      },
      orderBy: { invoiceDate: 'desc' },
    });
  }

  listAudits() {
    return this.prisma.stockAuditEntry.findMany({
      include: { company: true },
      orderBy: { auditDate: 'desc' },
    });
  }

  createAdjustment(dto: StockAdjustmentDto, userId: string) {
    return this.prisma.$transaction(async (tx) => {
      const totalAmount =
        Math.round(
          (dto.lines.reduce((sum, line) => sum + line.totalTp, 0) +
            Number.EPSILON) *
            100,
        ) / 100;
      const adjustment = await tx.stockAdjustment.create({
        data: {
          invoiceNo: dto.invoiceNo,
          invoiceDate: new Date(dto.invoiceDate),
          companyId: dto.companyId,
          branchId: dto.branchId,
          reason: dto.reason,
          lines: dto.lines.map((line) => ({ ...line })),
          totalAmount,
          createdById: userId,
        },
        include: {
          company: true,
          createdBy: {
            select: {
              id: true,
              username: true,
              firstName: true,
              lastName: true,
            },
          },
        },
      });
      for (const line of dto.lines) {
        // Positive adjustments add qty without increasing stock value (unit cost 0).
        await this.applyStockDelta(
          tx,
          dto.branchId,
          line.productId,
          line.netQty,
          MovementType.ADJUSTMENT,
          adjustment.id,
          dto.reason,
          line.netQty > 0 ? 0 : undefined,
        );
      }
      return adjustment;
    });
  }

  createAudit(dto: StockAuditDto, userId: string) {
    return this.prisma.$transaction(async (tx) => {
      const current = await tx.productBatch.aggregate({
        where: { branchId: dto.branchId, productId: dto.productId },
        _sum: { quantity: true },
      });
      const softwareQty = current._sum.quantity ?? 0;
      const variationQty = dto.physicalQty - softwareQty;
      const variationAmount =
        Math.round(
          (variationQty * dto.averagePrice + Number.EPSILON) * 100,
        ) / 100;
      const audit = await tx.stockAuditEntry.create({
        data: {
          ...dto,
          auditDate: new Date(dto.auditDate),
          softwareQty,
          variationQty,
          variationAmount,
          createdById: userId,
        },
        include: { company: true },
      });
      await this.applyStockDelta(
        tx,
        dto.branchId,
        dto.productId,
        variationQty,
        MovementType.ADJUSTMENT,
        audit.id,
        'Physical stock audit',
      );
      return audit;
    });
  }

  private async applyStockDelta(
    tx: Prisma.TransactionClient,
    branchId: string,
    productId: string,
    delta: number,
    type: MovementType,
    referenceId: string,
    notes: string,
    /** When set (incl. 0), positive qty uses this unit cost instead of catalog price. */
    unitCost?: number,
  ): Promise<void> {
    if (delta === 0) return;
    const batches = await tx.productBatch.findMany({
      where: { branchId, productId },
      orderBy: { expiryDate: 'asc' },
    });
    if (delta > 0) {
      const product = await tx.product.findUniqueOrThrow({
        where: { id: productId },
      });
      const inboundCost =
        unitCost === undefined ? Number(product.purchasePrice) : unitCost;
      const stockBefore = batches.reduce((sum, b) => sum + b.quantity, 0);
      const catalogBefore = Number(product.purchasePrice);
      const stockAfter = stockBefore + delta;
      const catalogAfter =
        stockAfter <= 0
          ? 0
          : Math.round(
              ((stockBefore * catalogBefore + delta * inboundCost) /
                stockAfter +
                Number.EPSILON) *
                100,
            ) / 100;

      let batch;
      if (batches[0]) {
        const existing = batches[0];
        const oldQty = existing.quantity;
        const oldPrice = Number(existing.purchasePrice);
        const newQty = oldQty + delta;
        const weighted =
          newQty <= 0
            ? 0
            : Math.round(
                ((oldQty * oldPrice + delta * inboundCost) / newQty +
                  Number.EPSILON) *
                  100,
              ) / 100;
        batch = await tx.productBatch.update({
          where: { id: existing.id },
          data: { quantity: newQty, purchasePrice: weighted },
        });
      } else {
        batch = await tx.productBatch.create({
          data: {
            productId,
            branchId,
            batchNumber: `ADJ-${Date.now().toString(36)}`,
            expiryDate: new Date('2099-12-31'),
            quantity: delta,
            purchasePrice: inboundCost,
            salePrice: product.salePrice,
          },
        });
      }

      if (catalogAfter !== catalogBefore) {
        await tx.product.update({
          where: { id: productId },
          data: { purchasePrice: catalogAfter },
        });
      }

      await tx.stockMovement.create({
        data: {
          productId,
          branchId,
          type,
          quantity: delta,
          unitCost: inboundCost,
          balanceAfter: batch.quantity,
          referenceType: 'StockRecord',
          referenceId,
          notes,
        },
      });
      return;
    }
    let remaining = Math.abs(delta);
    for (const existing of batches) {
      if (remaining === 0) break;
      const deduction = Math.min(existing.quantity, remaining);
      if (deduction === 0) continue;
      const batch = await tx.productBatch.update({
        where: { id: existing.id },
        data: { quantity: { decrement: deduction } },
      });
      await tx.stockMovement.create({
        data: {
          productId,
          branchId,
          type,
          quantity: -deduction,
          unitCost: Number(existing.purchasePrice),
          balanceAfter: batch.quantity,
          referenceType: 'StockRecord',
          referenceId,
          notes,
        },
      });
      remaining -= deduction;
    }
    if (remaining > 0) {
      throw new BadRequestException('Adjustment exceeds available stock');
    }
  }
}

@ApiTags('stock-records')
@ApiBearerAuth()
@Controller('stock-adjustments')
class StockAdjustmentsController {
  constructor(private readonly service: StockRecordsService) {}
  @Get()
  list() {
    return this.service.listAdjustments();
  }
  @Post()
  create(
    @Body() dto: StockAdjustmentDto,
    @CurrentUser() user: CurrentUserData,
  ) {
    return this.service.createAdjustment(dto, user.id);
  }
}

@ApiTags('stock-records')
@ApiBearerAuth()
@Controller('stock-audits')
class StockAuditsController {
  constructor(private readonly service: StockRecordsService) {}
  @Get()
  list() {
    return this.service.listAudits();
  }
  @Post()
  create(@Body() dto: StockAuditDto, @CurrentUser() user: CurrentUserData) {
    return this.service.createAudit(dto, user.id);
  }
}

@ApiTags('returns')
@ApiBearerAuth()
@Permissions('refund.ipd', 'refund.opd', 'refund.damage')
@Controller('returns')
class ReturnsController {
  constructor(private readonly service: ReturnsService) {}
  @Get()
  list() {
    return this.service.list();
  }
  @Post()
  create(@Body() dto: CreateReturnDto) {
    return this.service.create(dto);
  }
}

@ApiTags('reports')
@ApiBearerAuth()
@Permissions('reports.mis')
@Controller('reports')
class ReportsController {
  constructor(private readonly prisma: PrismaService) {}
  @Get('summary')
  async summary() {
    const [sales, purchases, payments] = await Promise.all([
      this.prisma.sale.aggregate({
        _sum: { total: true, paidAmount: true, dueAmount: true },
        _count: true,
      }),
      this.prisma.purchase.aggregate({
        _sum: { total: true, paidAmount: true },
        _count: true,
      }),
      this.prisma.payment.groupBy({
        by: ['method'],
        _sum: { amount: true },
        _count: true,
      }),
    ]);
    return { sales, purchases, payments };
  }
}

@ApiTags('dashboard')
@ApiBearerAuth()
@Permissions('dashboard.view')
@Controller('dashboard')
class DashboardController {
  constructor(private readonly prisma: PrismaService) {}
  @Get()
  async dashboard() {
    const now = new Date();
    const inThirtyDays = new Date(now.getTime() + 30 * 86_400_000);
    const [users, products, lowStock, expiring, todaySales] = await Promise.all(
      [
        this.prisma.user.count({ where: { isActive: true } }),
        this.prisma.product.count({ where: { deletedAt: null } }),
        this.prisma.productBatch.count({ where: { quantity: { lte: 5 } } }),
        this.prisma.productBatch.count({
          where: { expiryDate: { gte: now, lte: inThirtyDays } },
        }),
        this.prisma.sale.aggregate({
          where: {
            createdAt: {
              gte: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
            },
          },
          _sum: { total: true },
          _count: true,
        }),
      ],
    );
    return { users, products, lowStock, expiring, todaySales };
  }
}

@ApiTags('health')
@Controller('health')
class HealthController {
  constructor(private readonly prisma: PrismaService) {}
  @Public()
  @Get()
  async health() {
    try {
      await this.prisma.$queryRaw`SELECT 1`;
      return {
        status: 'ok',
        database: 'up',
        timestamp: new Date().toISOString(),
      };
    } catch {
      throw new ServiceUnavailableException('Database unavailable');
    }
  }
}

@Module({
  controllers: [
    ReturnsController,
    BillingCollectionsController,
    StockAdjustmentsController,
    StockAuditsController,
    ReportsController,
    DashboardController,
    HealthController,
  ],
  providers: [ReturnsService, StockRecordsService],
})
export class OperationsModule {}
